/**
 * TemplatesFacturePanel — LOT 3 : gestion des modèles de facture manuelle.
 * Liste + création/édition/suppression des templates (préfixe, objet, lignes).
 * Onglet « Modèles » de la brique compta (gestionnaires uniquement).
 */
import { useEffect, useState , type ReactNode } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { FactureTemplate, TemplateFactureBody } from '@/lib/api';
import { Plus, Trash2, Pencil, Save, X } from 'lucide-react';

// Wrapper léger (le composant Field du design-system impose htmlFor).
function FieldBox({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs font-medium text-ink-muted">{label}</label>
      {children}
    </div>
  );
}


type LigneForm = { libelle: string; quantite: number; prixUnitaire: number };

const EMPTY: TemplateFactureBody = {
  libelle: '', origineCode: '', objetDefaut: '', lignesDefaut: [], ordre: 0, actif: true,
};

export function TemplatesFacturePanel() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [templates, setTemplates] = useState<FactureTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Édition : null = pas d'édition ; 'new' = création ; sinon id.
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateFactureBody>(EMPTY);
  const [lignes, setLignes] = useState<LigneForm[]>([]);

  async function charger() {
    try {
      const r = await api.comptabilite.templatesFactures.list();
      setTemplates(r.templates);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement.'); }
  }
  useEffect(() => { void charger(); }, []);

  function ouvrirCreation() {
    setEditing('new');
    setForm({ ...EMPTY, ordre: templates.length + 1 });
    setLignes([{ libelle: '', quantite: 1, prixUnitaire: 0 }]);
    setError(null);
  }

  function ouvrirEdition(t: FactureTemplate) {
    setEditing(t.id);
    setForm({ libelle: t.libelle, origineCode: t.origineCode, objetDefaut: t.objetDefaut ?? '', lignesDefaut: [], ordre: t.ordre, actif: t.actif });
    setLignes(t.lignesDefaut.length ? t.lignesDefaut.map((l) => ({ ...l })) : [{ libelle: '', quantite: 1, prixUnitaire: 0 }]);
    setError(null);
  }

  function annuler() { setEditing(null); setError(null); }

  function majLigne(i: number, patch: Partial<LigneForm>) {
    setLignes((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function ajouterLigne() { setLignes((prev) => [...prev, { libelle: '', quantite: 1, prixUnitaire: 0 }]); }
  function retirerLigne(i: number) { setLignes((prev) => prev.filter((_, idx) => idx !== i)); }

  async function enregistrer() {
    setError(null);
    if (!form.libelle.trim()) { setError('Le libellé est obligatoire.'); return; }
    if (!form.origineCode.trim()) { setError("Le code d'origine (préfixe) est obligatoire."); return; }
    setBusy(true);
    const body: TemplateFactureBody = {
      libelle: form.libelle.trim(),
      origineCode: form.origineCode.trim().toUpperCase(),
      objetDefaut: form.objetDefaut?.trim() || null,
      lignesDefaut: lignes.filter((l) => l.libelle.trim()),
      ordre: form.ordre ?? 0,
      actif: form.actif ?? true,
    };
    try {
      if (editing === 'new') await api.comptabilite.templatesFactures.create(body);
      else if (editing) await api.comptabilite.templatesFactures.update(editing, body);
      setEditing(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : "Impossible d'enregistrer le modèle.");
    } finally { setBusy(false); }
  }

  async function supprimer(t: FactureTemplate) {
    const ok = await confirm({
      title: 'Supprimer ce modèle ?',
      description: `Le modèle « ${t.libelle} » sera définitivement supprimé. Les factures déjà émises ne sont pas affectées.`,
      confirmLabel: 'Supprimer', cancelLabel: 'Annuler', danger: true,
    });
    if (!ok) return;
    try { await api.comptabilite.templatesFactures.remove(t.id); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible.'); }
  }

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-muted">
          Modèles réutilisables pour créer une facture en un clic (préfixe du numéro, objet et lignes par défaut).
        </p>
        {!editing && (
          <Button variant="compta" onClick={ouvrirCreation}>
            <Plus className="mr-1 h-4 w-4" /> Nouveau modèle
          </Button>
        )}
      </div>

      {editing && (
        <Card variant="glass" padded>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-sm font-semibold text-ink">
              {editing === 'new' ? 'Nouveau modèle' : 'Modifier le modèle'}
            </h3>
            <button type="button" onClick={annuler} aria-label="Fermer sans enregistrer" className="text-ink-subtle hover:text-ink"><X className="h-4 w-4" aria-hidden="true" /></button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldBox label="Libellé du modèle">
              <input type="text" value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="Ex. Location de salle" />
            </FieldBox>
            <FieldBox label="Code d'origine (préfixe, ex. LOC)">
              <input type="text" value={form.origineCode} maxLength={8}
                onChange={(e) => setForm({ ...form, origineCode: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm uppercase text-ink" placeholder="LOC" />
            </FieldBox>
          </div>
          <FieldBox label="Objet par défaut" className="mt-3">
            <input type="text" value={form.objetDefaut ?? ''} onChange={(e) => setForm({ ...form, objetDefaut: e.target.value })}
              className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="Ex. Location de salle communale" />
          </FieldBox>

          <div className="mt-3">
            <div className="mb-1 text-xs font-medium text-ink-muted">Lignes par défaut</div>
            <div className="space-y-2">
              {lignes.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input type="text" value={l.libelle} onChange={(e) => majLigne(i, { libelle: e.target.value })}
                    className="h-9 flex-1 rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" placeholder="Libellé de la prestation" />
                  <input type="number" min={0} step="0.5" value={l.quantite} onChange={(e) => majLigne(i, { quantite: parseFloat(e.target.value) || 0 })}
                    className="h-9 w-20 rounded-r-md border border-border bg-surface/40 px-2 text-right text-sm text-ink" title="Quantité" />
                  <input type="number" min={0} step="0.01" value={l.prixUnitaire} onChange={(e) => majLigne(i, { prixUnitaire: parseFloat(e.target.value) || 0 })}
                    className="h-9 w-28 rounded-r-md border border-border bg-surface/40 px-2 text-right text-sm text-ink" title="Prix unitaire €" />
                  <button type="button" onClick={() => retirerLigne(i)} className="text-ink-subtle hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={ajouterLigne} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-compta">
              <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
            </button>
          </div>

          <div className="mt-3 flex items-center gap-4">
            <FieldBox label="Ordre d'affichage" className="w-32">
              <input type="number" min={0} value={form.ordre ?? 0} onChange={(e) => setForm({ ...form, ordre: parseInt(e.target.value, 10) || 0 })}
                className="h-9 w-full rounded-r-md border border-border bg-surface/40 px-2 text-sm text-ink" />
            </FieldBox>
            <label className="mt-5 inline-flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={form.actif ?? true} onChange={(e) => setForm({ ...form, actif: e.target.checked })} />
              Actif (proposé à la création)
            </label>
          </div>

          <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
            <Button variant="ghost" onClick={annuler}>Annuler</Button>
            <Button variant="compta" disabled={busy} onClick={() => void enregistrer()}>
              <Save className="mr-1 h-4 w-4" /> {busy ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="border-b border-border px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">Modèles de facture</h2>
        </div>
        {templates.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-subtle">Aucun modèle. Créez-en un pour accélérer la facturation.</p>
        ) : (
          <ul className="divide-y divide-border">
            {templates.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-ink">{t.libelle}</span>
                    <Badge variant="info" size="sm">{t.origineCode}</Badge>
                    {!t.actif && <Badge variant="neutral" size="sm">inactif</Badge>}
                  </div>
                  <div className="text-xs text-ink-subtle">
                    {t.objetDefaut || '—'} · {t.lignesDefaut.length} ligne{t.lignesDefaut.length > 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => ouvrirEdition(t)}
                    className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink">
                    <Pencil className="h-3.5 w-3.5" /> Modifier
                  </button>
                  <button type="button" onClick={() => void supprimer(t)}
                    className="inline-flex items-center gap-1 rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-danger">
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {dialogElement}
    </div>
  );
}
